/**
 * Robust Geolocation Service with Caching and Multi-Provider Fallback
 * Handles city detection for dynamic banner display
 */

class CacheManager {
  constructor(prefix = 'sc_geo') {
    this.prefix = prefix;
    this.geoDataKey = `${prefix}_data`;
    this.timestampKey = `${prefix}_timestamp`;
    this.ttl = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  }

  isSupported() {
    try {
      const testKey = '__localStorage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      return false;
    }
  }

  getCachedData() {
    if (!this.isSupported()) return null;

    try {
      const timestamp = localStorage.getItem(this.timestampKey);
      const data = localStorage.getItem(this.geoDataKey);

      if (!timestamp || !data) return null;

      const age = Date.now() - parseInt(timestamp);
      if (age > this.ttl) {
        this.clearCache();
        return null;
      }

      return JSON.parse(data);
    } catch (error) {
      console.warn('Error reading geo cache:', error);
      this.clearCache();
      return null;
    }
  }

  setCachedData(data) {
    if (!this.isSupported()) return false;

    try {
      localStorage.setItem(this.geoDataKey, JSON.stringify(data));
      localStorage.setItem(this.timestampKey, Date.now().toString());
      return true;
    } catch (error) {
      console.warn('Error setting geo cache:', error);
      return false;
    }
  }

  clearCache() {
    if (!this.isSupported()) return;

    try {
      localStorage.removeItem(this.geoDataKey);
      localStorage.removeItem(this.timestampKey);
    } catch (error) {
      console.warn('Error clearing geo cache:', error);
    }
  }

  getCacheAge() {
    if (!this.isSupported()) return null;

    try {
      const timestamp = localStorage.getItem(this.timestampKey);
      if (!timestamp) return null;

      return Date.now() - parseInt(timestamp);
    } catch (error) {
      return null;
    }
  }
}

class CityMapper {
  static cityMappings = {
    delhi: [
      'new delhi', 'delhi', 'noida', 'ghaziabad', 'gurugram', 'gurgaon', 
      'faridabad', 'dwarka', 'rohini', 'lajpat nagar', 'connaught place', 
      'karol bagh', 'saket', 'vasant kunj', 'janakpuri', 'pitampura',
      'mayur vihar', 'laxmi nagar', 'preet vihar', 'shahdara', 'ghazipur',
      'tilpat', 'greater noida', 'noida extension', 'sector', 'dlf',
      'cyber city', 'maneser', 'sohna', 'ballabhgarh', 'dādri'
    ],
    bengaluru: [
      'bengaluru', 'bangalore', 'bellary', 'hosur', 'indiranagar', 
      'koramangala', 'whitefield', 'electronic city', 'marathahalli',
      'hsr layout', 'btm layout', 'jayanagar', 'rajajinagar', 'malleshwaram',
      'yelahanka', 'banashankari', 'jp nagar', 'hebbal', 'sarjapur',
      'rr nagar', 'basaveshwara', 'banaswadi', 'kr puram', 'bommanahalli'
    ],
    mumbai: [
      'mumbai', 'bombay', 'andheri', 'bandra', 'juhu', 'worli', 'colaba',
      'powai', 'malad', 'borivali', 'thane', 'navi mumbai', 'vashi',
      'kharghar', 'panvel', 'kalyan', 'dombivli', 'mira road', 'virar',
      'goregaon', 'kandivali', 'dahisar', 'mulund', 'kurla', 'chembur'
    ],
    chennai: [
      'chennai', 'madras', 'tambaram', 'velachery', 'adyar', 'anna nagar',
      't nagar', 'nungambakkam', 'porur', 'omr', 'ecr', 'guindy',
      'perungudi', 'sholinganallur', 'chrompet', 'koyambedu',
      'thoraipakkam', 'kelambakkam', 'mahabalipuram', 'siruseri'
    ],
    pune: [
      'pune', 'pimpri chinchwad', 'pcmc', 'hinjewadi', 'wakad', 'baner',
      'aundh', 'viman nagar', 'koregaon park', 'camp', 'hadapsar',
      'magarpatta', 'kothrud', 'warje', 'karve nagar', 'deccan',
      'talegaon', 'chakan', 'ravet', 'nigdi', 'bhosari'
    ]
  };

  static detectCityRegion(cityName) {
    if (!cityName || typeof cityName !== 'string') return null;

    const normalizedCity = cityName.toLowerCase().trim();
    
    // Direct match first
    for (const [region, cities] of Object.entries(this.cityMappings)) {
      if (cities.includes(normalizedCity)) {
        return region;
      }
    }

    // Fuzzy matching - check if city contains any mapped location or vice versa
    for (const [region, cities] of Object.entries(this.cityMappings)) {
      for (const mappedCity of cities) {
        if (normalizedCity.includes(mappedCity) || mappedCity.includes(normalizedCity)) {
          return region;
        }
      }
    }

    return null;
  }

  static addCityMapping(region, cities) {
    if (!this.cityMappings[region]) {
      this.cityMappings[region] = [];
    }
    this.cityMappings[region].push(...cities);
  }

  static getSupportedRegions() {
    return Object.keys(this.cityMappings);
  }
}

class GeoLocationService {
  constructor() {
    this.cache = new CacheManager('sc_geo');
    this.providers = [
      
      {
        name: 'freeipapi',
        url: 'https://free.freeipapi.com/api/json',
        rateLimitRpm: 60,
        timeout: 9000,
        parser: (data) => ({
          city: data.cityName,
          region: data.region,
          country: data.country,
          countryCode: data.countryCode,
          latitude: data.latitude,
          longitude: data.longitude,
          timezone: data.timeZones ? data.timeZones[0] : null,                                      
          status: data.cityName ? 'success' : 'fail' 
        })
      },
      {
        name: 'ipinfo',
        url: 'https://ipinfo.io?token=d890bb604722c4',
        timeout: 5000,
        parser: (data) => ({
          city: data.city,
          region: data.region,
          country: data.country,
          countryCode: data.country,
          status: data.city ? 'success' : 'fail'
        })
      },
      {
        name: 'geojs',
        url: 'https://get.geojs.io/v1/ip/geo.json',
        rateLimitRpm: null, // No disclosed limit
        timeout: 5000,
        parser: (data) => ({
          city: data.city,
          region: data.region,
          country: data.country,
          countryCode: data.country_code,
          latitude: data.latitude,
          longitude: data.longitude,
          timezone: data.timezone,
          organization: data.organization_name,
          status: data.city ? 'success' : 'fail'
        })
      }
    ];
    
    this.retryConfig = {
      maxRetries: 2,
      baseDelay: 1000,
      maxDelay: 5000
    };
  }

  async detectCity() {
    try {
      // Step 1: Check cache first
      const cached = this.cache.getCachedData();
      if (cached && cached.city) {
        return CityMapper.detectCityRegion(cached.city);
      }



      // Step 2: Try each provider in order
      let geoData = null;
      for (const provider of this.providers) {
        try {
          geoData = await this.fetchFromProvider(provider);
          if (geoData && geoData.status === 'success' && geoData.city) {

            break;
          }
        } catch (error) {

          continue;
        }
      }

      // Step 3: Cache successful result
      if (geoData && geoData.city) {
        this.cache.setCachedData(geoData);
        return CityMapper.detectCityRegion(geoData.city);
      }


      return null;

    } catch (error) {
      return null;
    }
  }

  async fetchFromProvider(provider, retryCount = 0) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), provider.timeout);

    try {
      const response = await fetch(provider.url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const rawData = await response.json();
      const parsedData = provider.parser(rawData);

      if (parsedData.status !== 'success') {
        throw new Error(`Provider returned failure status: ${JSON.stringify(rawData)}`);
      }

      return parsedData;

    } catch (error) {
      clearTimeout(timeoutId);

      // Handle retry logic
      if (retryCount < this.retryConfig.maxRetries && !error.name === 'AbortError') {
        const delay = Math.min(
          this.retryConfig.baseDelay * Math.pow(2, retryCount),
          this.retryConfig.maxDelay
        );
        
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.fetchFromProvider(provider, retryCount + 1);
      }

      throw error;
    }
  }

  // Utility methods
  getCacheStatus() {
    const cached = this.cache.getCachedData();
    const age = this.cache.getCacheAge();
    
    return {
      hasCache: !!cached,
      cacheAge: age,
      cacheValid: age !== null && age < this.cache.ttl,
      cachedCity: cached ? cached.city : null
    };
  }

  clearCache() {
    this.cache.clearCache();
  }

  getSupportedCities() {
    return CityMapper.getSupportedRegions();
  }

  addCityMapping(region, cities) {
    CityMapper.addCityMapping(region, cities);
  }
}

// Enhanced Banner Manager with Progressive Enhancement
class BannerManager {
  constructor() {
    this.defaultBannerSelector = 'a.media-box.customizer.default';
    this.cityBannerSelector = 'a.media-box.customizer';
    this.allCustomizerSelector = '.customizer';
    this.currentBanner = 'default';
    this.isInitialized = false;
  }

  // Immediate cache-based banner switching (synchronous)
  showBannerFromCache() {
    try {
      const cacheManager = new CacheManager('sc_geo');
      const cached = cacheManager.getCachedData();
      
      if (cached && cached.city) {
        const region = CityMapper.detectCityRegion(cached.city);
        if (region) {
          
          this.switchBanner(region, true);
          return region;
        }
      }
      
      // Ensure default is visible if no cache
      this.ensureDefaultVisible();
      return 'default';
      
    } catch (error) {
      
      this.ensureDefaultVisible();
      return 'default';
    }
  }

  // Graceful banner switching with smooth transitions
  switchBanner(targetRegion, isFromCache = false) {
    try {
      const targetBanner = document.querySelector(`${this.cityBannerSelector}.${targetRegion}`);
      const defaultBanner = document.querySelector(this.defaultBannerSelector);
      
      // If target banner doesn't exist, keep default
      if (!targetBanner && targetRegion !== 'default') {
        
        this.trackBannerImpression('default');
        return false;
      }

      // If already showing the correct banner, do nothing
      if (this.currentBanner === targetRegion) {
        return true;
      }

      // Perform the switch
      if (targetRegion === 'default' || !targetBanner) {
        this.showDefaultBanner(defaultBanner);
      } else {
        this.showCityBanner(targetBanner, defaultBanner, targetRegion, isFromCache);
      }

      this.currentBanner = targetRegion;
      this.trackBannerImpression(targetRegion);
      return true;

    } catch (error) {
      this.ensureDefaultVisible();
      return false;
    }
  }

  showDefaultBanner(defaultBanner) {
    // Hide all city banners
    const allCityBanners = document.querySelectorAll(`${this.allCustomizerSelector}:not(.default)`);
    allCityBanners.forEach(banner => {
      banner.classList.remove('geo-visible');
      banner.classList.add('geo-hidden');
    });

    // Show default
    if (defaultBanner) {
      defaultBanner.classList.remove('geo-hidden');
      defaultBanner.classList.add('geo-visible');
    }
  }

  showCityBanner(cityBanner, defaultBanner, region, isFromCache) {
    // If transitioning from cache, make it instant
    const transitionClass = isFromCache ? 'geo-instant' : 'geo-transitioning';
    
    // Add transition class
    cityBanner.classList.add(transitionClass);
    
    // Hide default banner
    if (defaultBanner) {
      defaultBanner.classList.remove('geo-visible');
      defaultBanner.classList.add('geo-hidden');
    }

    // Hide other city banners
    const otherCityBanners = document.querySelectorAll(`${this.allCustomizerSelector}:not(.default):not(.${region})`);
    otherCityBanners.forEach(banner => {
      banner.classList.remove('geo-visible');
      banner.classList.add('geo-hidden');
    });

    // Show target city banner
    setTimeout(() => {
      cityBanner.classList.remove('geo-hidden');
      cityBanner.classList.add('geo-visible');
      
      // Remove transition class after animation
      setTimeout(() => {
        cityBanner.classList.remove(transitionClass);
      }, 300);
    }, isFromCache ? 0 : 50);

  }

  ensureDefaultVisible() {
    const defaultBanner = document.querySelector(this.defaultBannerSelector);
    if (defaultBanner) {
      defaultBanner.classList.remove('geo-hidden');
      defaultBanner.classList.add('geo-visible');
    }

    // Hide all city banners
    const allCityBanners = document.querySelectorAll(`${this.allCustomizerSelector}:not(.default)`);
    allCityBanners.forEach(banner => {
      banner.classList.add('geo-hidden');
      banner.classList.remove('geo-visible');
    });
  }

  // Public method for API-driven updates
  showBannerForCity(detectedRegion) {
    return this.switchBanner(detectedRegion || 'default', false);
  }

  trackBannerImpression(region) {
    try {
      // Google Analytics tracking
      if (typeof gtag !== 'undefined') {
        gtag('event', 'banner_impression', {
          event_category: 'geo_targeting',
          event_label: region,
          custom_map: { region: region }
        });
      }

      // DataLayer tracking (existing pattern in the code)
      if (typeof dataLayer !== 'undefined') {
        dataLayer.push({
          event: 'geo_banner_impression',
          banner_region: region,
          timestamp: new Date().toISOString()
        });
      }
    } catch (error) {
    }
  }
}

// Initialize and expose global instance
window.SleepyCatGeo = {
  service: new GeoLocationService(),
  bannerManager: new BannerManager(),
  
  // Progressive enhancement initialization
  async init() {
    try {
      
      
      // PHASE 1: Immediate cache-based banner (synchronous)
      const cachedRegion = this.bannerManager.showBannerFromCache();
      
      
      // PHASE 2: Background API enhancement (asynchronous)
      const apiRegion = await this.service.detectCity();
      
      
      // PHASE 3: Graceful update if needed
      let finalRegion = cachedRegion;
      let bannerChanged = false;
      
      if (apiRegion && apiRegion !== cachedRegion) {
        
        bannerChanged = this.bannerManager.showBannerForCity(apiRegion);
        finalRegion = apiRegion;
      } else if (!apiRegion && cachedRegion === 'default') {
        // Ensure default is properly shown if API fails and no cache
        this.bannerManager.ensureDefaultVisible();
      }
      
      
      
      return {
        region: finalRegion,
        bannerChanged: bannerChanged,
        cacheStatus: this.service.getCacheStatus(),
        wasFromCache: !bannerChanged && cachedRegion !== 'default'
      };
      
    } catch (error) {
      
      
      // Emergency fallback - ensure default banner is visible
      this.bannerManager.ensureDefaultVisible();
      
      return {
        region: 'default',
        bannerChanged: false,
        error: error.message,
        fallback: true
      };
    }
  },

  // Manual cache refresh (for testing/debugging)
  async refresh() {
    this.service.clearCache();
    return await this.init();
  },

  // Get current banner state
  getCurrentBanner() {
    return this.bannerManager.currentBanner;
  }
};

